/**
 * Apps autonomes - liste des applications installables, pour la promotion croisée
 *
 * Une app autonome = un module servi sur sa propre adresse (depenses.cabaza.eu…).
 * Les adresses viennent de `domains` (moduleid renseigné, une par langue) ; le nom,
 * l'icône et la couleur viennent de `modules`. Aucune route dédiée : les deux routes
 * publiques existantes suffisent.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

const CACHE_KEY = 'cabazaAutonomousApps.v1';
const CACHE_TTL_MS = 10 * 60 * 1000;

const lire = (objet, nom) => objet?.[nom] ?? objet?.[nom.toUpperCase()] ?? objet?.[nom.toLowerCase()];

const jsonList = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const result = await response.json();
    const data = Array.isArray(result) ? result : (lire(result, 'data') ?? []);
    return Array.isArray(data) ? data : [];
};

const charger = async () => {
    try {
        const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
        if (cached && Date.now() - cached.at < CACHE_TTL_MS) return cached;
    } catch { /* cache absent ou illisible */ }

    const [domains, modules] = await Promise.all([
        jsonList(`${API_BASE_URL}/domains`),
        jsonList(`${API_BASE_URL}/modules?lang=fr&isActive=1`)
    ]);
    const donnees = { at: Date.now(), domains, modules };
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(donnees)); } catch { /* quota */ }
    return donnees;
};

/**
 * Les apps autonomes disponibles, une entrée par module, dans la langue demandée.
 *
 * Pour chaque module, l'adresse retenue est celle de la langue de l'utilisateur ; à
 * défaut, l'anglais (la cible du repli côté serveur), puis la première adresse active.
 *
 * @param {string} language - Code de langue de l'interface (fr, en, nl…)
 * @param {string|null} excludeModuleCode - Module de l'app courante, à ne pas proposer
 * @returns {Promise<Array<{code, name, url, iconUrl, color}>>} Vide en cas d'échec
 */
export const getAutonomousApps = async (language = 'fr', excludeModuleCode = null) => {
    try {
        const { domains, modules } = await charger();
        const exclu = String(excludeModuleCode || '').toUpperCase();

        const parModule = new Map();
        for (const d of domains) {
            const moduleCode = String(lire(d, 'modulecode') || '').toUpperCase();
            if (!moduleCode || !Number(lire(d, 'isactive') ?? 1)) continue;
            if (!parModule.has(moduleCode)) parModule.set(moduleCode, []);
            parModule.get(moduleCode).push(d);
        }

        const apps = [];
        for (const [code, adresses] of parModule) {
            if (code === exclu) continue;
            const choisie =
                adresses.find(a => lire(a, 'defaultlanguagecode') === language) ||
                adresses.find(a => lire(a, 'defaultlanguagecode') === 'en') ||
                adresses[0];
            const module = modules.find(m => String(lire(m, 'code')).toUpperCase() === code);
            if (!module) continue;

            const iconPath = lire(module, 'appiconpath');
            const couleur = lire(module, 'appthemecolor') || lire(module, 'color') || '#9333ea';
            apps.push({
                code,
                name: lire(module, 'appname') || lire(module, `name_${language}`) || lire(module, 'name_fr') || code,
                url: `https://${lire(choisie, 'domainname')}`,
                iconUrl: iconPath ? `${FILES_BASE_URL}/${iconPath}` : null,
                color: couleur
            });
        }
        return apps;
    } catch (error) {
        console.error('Error fetching autonomous apps:', error);
        return [];
    }
};

/**
 * La description courte d'un module dans la langue demandée (page publique d'une app).
 * Repli sur l'anglais puis le français ; chaîne vide en cas d'échec.
 */
export const getAppDescription = async (moduleCode, language = 'fr') => {
    try {
        const { modules } = await charger();
        const module = modules.find(m => String(lire(m, 'code')).toUpperCase() === String(moduleCode || '').toUpperCase());
        if (!module) return '';
        return lire(module, `shortdescription_${language}`) || lire(module, 'shortdescription_en') || lire(module, 'shortdescription_fr') || '';
    } catch {
        return '';
    }
};
